import React from 'react';
import { useLocation, matchPath } from 'react-router-dom';

import { Flex, Text, Tab } from '@opendoor/bricks/core';

import { getHubOverviewRoute } from 'shared/helpers/routes';
import { useOfferContext } from 'sellDirect/OfferContext';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { NavLinkWithMetrics } from 'sellDirect/components/legacy/layout/NavLinkWithMetrics';
import { MONTH_DAY_FORMAT } from 'shared/helpers/datetime';

export type NavBarItems = Array<{ name: string; link: string }>;

/** Legacy navigation bar */
export const NavBar: React.FC<{ navBarItems: NavBarItems; selectedNavBarItem?: string }> = ({
  navBarItems,
  selectedNavBarItem,
}) => {
  return (
    <Flex flexDirection="column" py={4} alignItems="stretch">
      {navBarItems.map(({ name, link }) => {
        return (
          <NavLink key={name} navName={name} link={link} selectedNavBarItem={selectedNavBarItem} />
        );
      })}
    </Flex>
  );
};

const NavLink: React.FC<{
  link: string;
  selectedNavBarItem?: string;
  navName: string;
  isDisabled?: boolean;
  newTab?: boolean;
  awayFromCake?: boolean;
  Icon?: React.FC;
}> = ({ link, selectedNavBarItem, navName, isDisabled, newTab, awayFromCake, Icon }) => {
  const { offer } = useOfferContext();
  const location = useLocation();
  const shouldShowExpiration = navName === 'Preliminary offer' && offer?.isExpired;
  const match = matchPath(location.pathname, link);
  const isActive = !!(match && selectedNavBarItem && link.includes(selectedNavBarItem));

  const renderLink = () => (
    <Flex alignItems="center" data-testid={navName}>
      {/* TODO @sellDirect - Semantic links without href p props are a11y violations. See https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1943339236/Accessibility+best+practices+for+frontend+development#Common-anti-patterns-to-look-out-for-in-progress */}
      <Tab
        /**
         * TODO @platform:
         * Semantic links without href p props are a11y violations. See https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1943339236/Accessibility+best+practices+for+frontend+development#Common-anti-patterns-to-look-out-for-in-progress
         */

        variant="nav-item"
        isActive={isActive}
        isDisabled={isDisabled}
        display={shouldShowExpiration ? 'block' : 'flex'}
        alignItems="center"
        // empty because this is wrapped in a link (anti-pattern)
        analyticsName=""
        aria-label={null}
      >
        {navName}
        {Icon && <Icon />}
        {shouldShowExpiration && (
          <Text
            as="p"
            fontWeight="bold"
            fontSize="s000"
            // @ts-expect-error TODO @sellDirect Use a standard lineHeight (e.g., "s000")
            lineHeight="8px"
            color="brand50"
            pt={3}
            textTransform="uppercase"
          >
            Expired {offer?.expiresAt?.format(MONTH_DAY_FORMAT)}
          </Text>
        )}
      </Tab>
    </Flex>
  );
  return (
    <>
      {isDisabled ? (
        renderLink()
      ) : (
        <NavLinkWithMetrics
          label={`nav-link-${navName}`}
          to={link}
          key={link}
          newTab={newTab}
          awayFromCake={awayFromCake}
        >
          {renderLink()}
        </NavLinkWithMetrics>
      )}
    </>
  );
};

/** TODO: Add documentation */
export const useNavBarLinks = (): NavBarItems => {
  const { isREITQualified, loadingQualification } = useQualificationContext();

  if (loadingQualification) {
    return [];
  }

  const navBarItems: NavBarItems = [{ name: 'Overview', link: getHubOverviewRoute() }];
  if (isREITQualified) {
    return navBarItems.concat([
      {
        name: 'My home',
        link: `${getHubOverviewRoute()}#my-home`,
      },
      {
        name: 'Live trends',
        link: `${getHubOverviewRoute()}#live-trends`,
      },
    ]);
  }

  return navBarItems;
};
